import React from 'react'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  size?: 'small' | 'medium' | 'large' | 'xl' | 'full'
}

export default function Modal({ isOpen, onClose, title, children, size = 'medium' }: ModalProps) {
  if (!isOpen) return null

  const sizeClasses = {
    small: 'sm:max-w-md',
    medium: 'sm:max-w-lg',
    large: 'sm:max-w-4xl',
    xl: 'sm:max-w-6xl',
    full: 'sm:max-w-none sm:w-full sm:h-full'
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* This element is to trick the browser into centering the modal contents. */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        {/* Modal panel */}
        <div className={`inline-block align-bottom bg-white ${size === 'full' ? 'rounded-none' : 'rounded-lg'} text-left shadow-xl transform transition-all ${size === 'full' ? 'sm:my-0 sm:align-top sm:h-screen overflow-hidden' : 'sm:my-8 sm:align-middle overflow-hidden'} ${sizeClasses[size]} sm:w-full`}>
          <div className={`bg-white ${size === 'full' ? 'px-6 pt-6 pb-4 h-full flex flex-col overflow-hidden' : 'px-4 pt-5 pb-4 sm:p-6 sm:pb-4'}`}>
            <div className={`${size === 'full' ? 'flex-shrink-0' : 'sm:flex sm:items-start'}`}>
              <div className={`${size === 'full' ? 'w-full' : 'mt-3 text-center sm:mt-0 sm:text-left w-full'}`}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {title}
                  </h3>
                  {size === 'full' && (
                    <button
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
            {/* Content area - scrollable for full size modals */}
            <div className={`${size === 'full' ? 'flex-1 overflow-auto min-h-0' : 'mt-2'}`}>
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}